import React, { useEffect, useState } from 'react';
import { ArrowLeft, Bell, CalendarRange, Check, Copy, Dumbbell, Flag, Loader2, Pencil, Plus, Save, Smartphone, Trash2 } from 'lucide-react';
import { Race, User } from '../types';
import { supabase } from '../services/auth';
import { addRace, daysUntil, deleteRace, listRaces, updateRace } from '../services/races';
import { ServiceConnection } from './ServiceConnection';
import { Logo } from './Logo';

interface SettingsProps {
  user: User;
  onUpdate: (updatedUser: User) => void;
  onBack: () => void;
}

export function Settings({ user, onUpdate, onBack }: SettingsProps) {
  const [formData, setFormData] = useState<Partial<User>>({
    fitnessGoals: user.fitnessGoals,
    activityPreferences: user.activityPreferences,
    weeklyTrainingVolume: user.weeklyTrainingVolume,
    notificationPreferences: user.notificationPreferences,
    weight: user.weight,
    trainingDays: user.trainingDays ?? [],
    typicalSessionMinutes: user.typicalSessionMinutes,
    birthYear: user.birthYear,
    injuryNotes: user.injuryNotes ?? '',
    lifeContext: user.lifeContext ?? '',
    experienceLevel: user.experienceLevel,
    maxHr: user.maxHr,
    thresholdPace: user.thresholdPace ?? '',
    trainingPlanIcalUrl: user.trainingPlanIcalUrl ?? ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const [races, setRaces] = useState<Race[]>([]);
  const [newRace, setNewRace] = useState({ name: '', race_date: '', distance_miles: '', priority: 'B' as Race['priority'], goal: '' });
  const [raceError, setRaceError] = useState<string | null>(null);
  const [addingRace, setAddingRace] = useState(false);
  const [copiedIngestUrl, setCopiedIngestUrl] = useState(false);
  const ingestUrl = user.ingestKey
    ? `${window.location.origin}/.netlify/functions/apple-health-ingest?key=${user.ingestKey}`
    : null;

  const copyIngestUrl = async () => {
    if (!ingestUrl) return;
    await navigator.clipboard.writeText(ingestUrl);
    setCopiedIngestUrl(true);
    setTimeout(() => setCopiedIngestUrl(false), 1500);
  };

  const [editingRaceId, setEditingRaceId] = useState<string | null>(null);
  const [raceEdit, setRaceEdit] = useState({ goal: '', priority: 'B' as Race['priority'] });
  const [savingRaceEdit, setSavingRaceEdit] = useState(false);

  const startRaceEdit = (race: Race) => {
    setEditingRaceId(race.id);
    setRaceEdit({ goal: race.goal ?? '', priority: race.priority });
  };

  const saveRaceEdit = async (id: string) => {
    setSavingRaceEdit(true);
    setRaceError(null);
    try {
      const updated = await updateRace(id, {
        goal: raceEdit.goal.trim() || null,
        priority: raceEdit.priority,
      });
      setRaces((prev) => prev.map((r) => (r.id === id ? updated : r)));
      setEditingRaceId(null);
    } catch (err) {
      console.error('Failed to update race:', err);
      setRaceError('Could not update the race. Please try again.');
    } finally {
      setSavingRaceEdit(false);
    }
  };

  useEffect(() => {
    listRaces().then(setRaces).catch((err) => console.error('Failed to load races:', err));
  }, []);

  const handleAddRace = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRace.name || !newRace.race_date) return;
    setAddingRace(true);
    setRaceError(null);
    try {
      const created = await addRace({
        name: newRace.name,
        race_date: newRace.race_date,
        distance_miles: newRace.distance_miles ? Number(newRace.distance_miles) : null,
        priority: newRace.priority,
        goal: newRace.goal.trim() || null,
      });
      setRaces((prev) =>
        [...prev, created].sort((a, b) => a.race_date.localeCompare(b.race_date))
      );
      setNewRace({ name: '', race_date: '', distance_miles: '', priority: 'B', goal: '' });
    } catch (err) {
      console.error('Failed to add race:', err);
      setRaceError('Could not add the race. Please try again.');
    } finally {
      setAddingRace(false);
    }
  };

  const handleDeleteRace = async (id: string) => {
    const previous = races;
    setRaces((prev) => prev.filter((r) => r.id !== id));
    try {
      await deleteRace(id);
    } catch (err) {
      console.error('Failed to delete race:', err);
      setRaces(previous);
    }
  };

  const activityOptions = [
    'Running',
    'Cycling',
    'Swimming',
    'Weight Training',
    'HIIT',
    'Yoga',
    'Pilates',
    'Rock Climbing',
    'CrossFit',
    'Hiking'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      if (name === 'smsNotifications') {
        setFormData(prev => ({
          ...prev,
          notificationPreferences: {
            ...prev.notificationPreferences!,
            sms: checkbox.checked
          }
        }));
      }
    } else if (name === 'fitnessGoals.primary') {
      setFormData(prev => ({
        ...prev,
        fitnessGoals: {
          ...prev.fitnessGoals!,
          primary: value as User['fitnessGoals']['primary']
        }
      }));
    } else if (name === 'fitnessGoals.secondary') {
      setFormData(prev => ({
        ...prev,
        fitnessGoals: {
          ...prev.fitnessGoals!,
          secondary: (value || undefined) as User['fitnessGoals']['secondary']
        }
      }));
    } else if (name === 'fitnessGoals.weeklyWorkouts') {
      setFormData(prev => ({
        ...prev,
        fitnessGoals: {
          ...prev.fitnessGoals!,
          weeklyWorkouts: parseInt(value)
        }
      }));
    } else if (name === 'fitnessGoals.targetIntensity') {
      setFormData(prev => ({
        ...prev,
        fitnessGoals: {
          ...prev.fitnessGoals!,
          targetIntensity: value as 'low' | 'moderate' | 'high'
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'number' ? Number(value) : value
      }));
    }
  };

  const toggleActivity = (activity: string) => {
    setFormData(prev => {
      const current = prev.activityPreferences || [];
      return {
        ...prev,
        activityPreferences: current.includes(activity)
          ? current.filter(a => a !== activity)
          : [...current, activity]
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveMessage(null);

    try {
      const estimatedVolume =
        formData.trainingDays?.length && formData.typicalSessionMinutes
          ? Math.round((formData.trainingDays.length * formData.typicalSessionMinutes) / 60)
          : formData.weeklyTrainingVolume ?? 0;

      const { error } = await supabase
        .from('users')
        .update({
          fitness_goals: formData.fitnessGoals,
          activity_preferences: formData.activityPreferences,
          weekly_training_volume: estimatedVolume,
          notification_preferences: formData.notificationPreferences,
          weight: formData.weight,
          training_days: formData.trainingDays ?? [],
          typical_session_minutes: formData.typicalSessionMinutes ?? null,
          birth_year: formData.birthYear ?? null,
          injury_notes: formData.injuryNotes?.trim() || null,
          life_context: formData.lifeContext?.trim() || null,
          experience_level: formData.experienceLevel ?? null,
          max_hr: formData.maxHr ?? null,
          threshold_pace: formData.thresholdPace?.trim() || null,
          training_plan_ical_url: formData.trainingPlanIcalUrl?.trim() || null
        })
        .eq('id', user.id);

      if (error) throw error;

      onUpdate({ ...user, ...formData, weeklyTrainingVolume: estimatedVolume });
      setSaveMessage({ type: 'success', text: 'Settings saved!' });

      // Automatically return to dashboard after successful save
      setTimeout(() => {
        onBack();
      }, 1500);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <header className="sticky top-0 z-10 border-b border-zinc-950/5 bg-zinc-50/80 backdrop-blur dark:border-white/5 dark:bg-zinc-950/80">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-3.5">
          <Logo />
          <button onClick={onBack} className="btn-ghost">
            <ArrowLeft className="h-4 w-4" />
            Back to dashboard
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-8">
        <h1 className="mb-6 text-2xl font-bold tracking-tight text-zinc-900 dark:text-white">
          Settings
        </h1>

        {saveMessage && (
          <div
            className={`mb-6 flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium ring-1 ${
              saveMessage.type === 'success'
                ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/10 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-400/20'
                : 'bg-rose-50 text-rose-700 ring-rose-600/10 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-400/20'
            }`}
          >
            {saveMessage.type === 'success' && <Check className="h-4 w-4" />}
            {saveMessage.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <section className="card p-6 sm:p-8">
            <h2 className="mb-5 flex items-center gap-2 text-base font-semibold text-zinc-900 dark:text-white">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600/10 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-400">
                <Dumbbell className="h-4 w-4" />
              </span>
              Fitness goals
            </h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label className="label">Primary goal</label>
                <select
                  name="fitnessGoals.primary"
                  value={formData.fitnessGoals?.primary}
                  onChange={handleInputChange}
                  className="input select"
                >
                  <option value="general-fitness">General Fitness</option>
                  <option value="strength">Build Strength</option>
                  <option value="endurance">Improve Endurance</option>
                  <option value="weight-loss">Weight Loss</option>
                </select>
              </div>
              <div>
                <label className="label">Also working on <span className="font-normal text-zinc-400">(optional)</span></label>
                <select
                  name="fitnessGoals.secondary"
                  value={formData.fitnessGoals?.secondary || ''}
                  onChange={handleInputChange}
                  className="input select"
                >
                  <option value="">None</option>
                  <option value="general-fitness">General Fitness</option>
                  <option value="strength">Build Strength</option>
                  <option value="endurance">Improve Endurance</option>
                  <option value="weight-loss">Weight Loss</option>
                </select>
              </div>
              <div>
                <label className="label">Weekly workouts</label>
                <input
                  type="number"
                  name="fitnessGoals.weeklyWorkouts"
                  value={formData.fitnessGoals?.weeklyWorkouts}
                  onChange={handleInputChange}
                  className="input"
                  min="1"
                  max="7"
                />
              </div>
              <div>
                <label className="label">Target intensity</label>
                <select
                  name="fitnessGoals.targetIntensity"
                  value={formData.fitnessGoals?.targetIntensity}
                  onChange={handleInputChange}
                  className="input select"
                >
                  <option value="low">Low</option>
                  <option value="moderate">Moderate</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <label className="label">Weight (lbs)</label>
                <input
                  type="number"
                  name="weight"
                  value={formData.weight}
                  onChange={handleInputChange}
                  className="input"
                  min="50"
                  max="500"
                />
              </div>
              <div>
                <label className="label">Birth year</label>
                <input
                  type="number"
                  name="birthYear"
                  value={formData.birthYear || ''}
                  onChange={handleInputChange}
                  className="input"
                  placeholder="1992"
                  min="1920"
                  max={new Date().getFullYear() - 10}
                />
              </div>
            </div>

            <div className="mt-5">
              <label className="label">Training days</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'mon', label: 'Mon' },
                  { key: 'tue', label: 'Tue' },
                  { key: 'wed', label: 'Wed' },
                  { key: 'thu', label: 'Thu' },
                  { key: 'fri', label: 'Fri' },
                  { key: 'sat', label: 'Sat' },
                  { key: 'sun', label: 'Sun' },
                ].map((d) => {
                  const selected = formData.trainingDays?.includes(d.key);
                  return (
                    <button
                      key={d.key}
                      type="button"
                      onClick={() =>
                        setFormData((prev) => {
                          const current = prev.trainingDays || [];
                          return {
                            ...prev,
                            trainingDays: current.includes(d.key)
                              ? current.filter((x) => x !== d.key)
                              : [...current, d.key],
                          };
                        })
                      }
                      className={`chip ${selected ? 'chip-on' : 'chip-off'}`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-5">
              <label className="label">Typical session length</label>
              <div className="flex flex-wrap gap-2">
                {[30, 45, 60, 90].map((mins) => (
                  <button
                    key={mins}
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, typicalSessionMinutes: mins }))}
                    className={`chip ${formData.typicalSessionMinutes === mins ? 'chip-on' : 'chip-off'}`}
                  >
                    {mins} min
                  </button>
                ))}
              </div>
            </div>
          </section>

          <section className="card p-6 sm:p-8">
            <h2 className="mb-2 text-base font-semibold text-zinc-900 dark:text-white">
              About you
            </h2>
            <p className="mb-5 text-sm text-zinc-500 dark:text-zinc-400">
              All optional — but everything here makes your coach noticeably
              smarter about your recommendations.
            </p>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="label">Life context</label>
                <textarea
                  name="lifeContext"
                  value={formData.lifeContext}
                  onChange={(e) => setFormData((prev) => ({ ...prev, lifeContext: e.target.value }))}
                  className="input min-h-[70px]"
                  placeholder='e.g. "New baby at home — sleep is unpredictable. Mornings are my only reliable window."'
                />
              </div>
              <div className="sm:col-span-2">
                <label className="label">Injuries or movements to avoid</label>
                <textarea
                  name="injuryNotes"
                  value={formData.injuryNotes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, injuryNotes: e.target.value }))}
                  className="input min-h-[70px]"
                  placeholder='e.g. "Left knee grumbles on downhills. Rebuilding core postpartum — no heavy loaded twisting yet."'
                />
              </div>
              <div>
                <label className="label">Experience level</label>
                <select
                  name="experienceLevel"
                  value={formData.experienceLevel || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      experienceLevel: (e.target.value || undefined) as User['experienceLevel'],
                    }))
                  }
                  className="input select"
                >
                  <option value="">Prefer not to say</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
              <div>
                <label className="label">Max heart rate <span className="font-normal text-zinc-400">(if known)</span></label>
                <input
                  type="number"
                  name="maxHr"
                  value={formData.maxHr || ''}
                  onChange={handleInputChange}
                  className="input"
                  placeholder="e.g. 188"
                  min="120"
                  max="230"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="label">Threshold pace or power <span className="font-normal text-zinc-400">(if known)</span></label>
                <input
                  type="text"
                  name="thresholdPace"
                  value={formData.thresholdPace}
                  onChange={(e) => setFormData((prev) => ({ ...prev, thresholdPace: e.target.value }))}
                  className="input"
                  placeholder='e.g. "8:45/mi half-marathon pace" or "205W FTP"'
                />
              </div>
            </div>
          </section>

          <section className="card p-6 sm:p-8">
            <h2 className="mb-5 text-base font-semibold text-zinc-900 dark:text-white">
              Activity preferences
            </h2>
            <div className="flex flex-wrap gap-2">
              {activityOptions.map(activity => {
                const selected = formData.activityPreferences?.includes(activity);
                return (
                  <button
                    key={activity}
                    type="button"
                    onClick={() => toggleActivity(activity)}
                    className={`chip ${selected ? 'chip-on' : 'chip-off'}`}
                  >
                    {selected && <Check className="mr-1 inline h-3.5 w-3.5" />}
                    {activity}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="card p-6 sm:p-8">
            <h2 className="mb-5 flex items-center gap-2 text-base font-semibold text-zinc-900 dark:text-white">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600/10 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-400">
                <Bell className="h-4 w-4" />
              </span>
              Notifications
            </h2>
            <label className="flex cursor-pointer items-start gap-3 rounded-xl bg-zinc-50 p-4 ring-1 ring-zinc-950/5 dark:bg-zinc-800/60 dark:ring-white/5">
              <input
                type="checkbox"
                name="smsNotifications"
                checked={formData.notificationPreferences?.sms}
                onChange={handleInputChange}
                className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-600"
              />
              <span className="text-sm text-zinc-700 dark:text-zinc-300">
                <span className="font-medium">Daily SMS recommendations</span>
                <span className="block text-zinc-500 dark:text-zinc-400">
                  Get each morning&apos;s workout as a text message.
                </span>
              </span>
            </label>
          </section>

          <section className="card p-6 sm:p-8">
            <h2 className="mb-2 flex items-center gap-2 text-base font-semibold text-zinc-900 dark:text-white">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600/10 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-400">
                <Flag className="h-4 w-4" />
              </span>
              Race calendar
            </h2>
            <p className="mb-5 text-sm text-zinc-500 dark:text-zinc-400">
              Your coach periodizes training toward these — builds, tapers, and
              recovery all follow this calendar. Priority: A = goal race,
              B = important, C = supported workout.
            </p>

            {races.length > 0 && (
              <ul className="mb-5 space-y-2">
                {races.map((race) => (
                  <li
                    key={race.id}
                    className="rounded-xl bg-zinc-50 px-4 py-3 ring-1 ring-zinc-950/5 dark:bg-zinc-800/60 dark:ring-white/5"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-zinc-900 dark:text-white">
                          {race.name}
                          <span className="ml-2 rounded bg-indigo-600/10 px-1.5 py-0.5 text-[10px] font-bold text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-300">
                            {race.priority}
                          </span>
                        </p>
                        <p className="text-xs text-zinc-500 dark:text-zinc-400">
                          {new Date(`${race.race_date}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                          {race.distance_miles ? ` · ${race.distance_miles} mi` : ''}
                          {` · in ${daysUntil(race.race_date)} days`}
                          {race.goal ? ` · goal: ${race.goal}` : ''}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          onClick={() =>
                            editingRaceId === race.id ? setEditingRaceId(null) : startRaceEdit(race)
                          }
                          className="btn-ghost px-2"
                          aria-label={`Edit ${race.name}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteRace(race.id)}
                          className="btn-ghost px-2 text-zinc-400 hover:text-rose-600 dark:hover:text-rose-400"
                          aria-label={`Remove ${race.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {editingRaceId === race.id && (
                      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-zinc-950/5 pt-3 dark:border-white/5">
                        <input
                          type="text"
                          value={raceEdit.goal}
                          onChange={(e) => setRaceEdit((p) => ({ ...p, goal: e.target.value }))}
                          className="input flex-1 min-w-[160px]"
                          placeholder='Goal — e.g. "3:55" or "finish strong"'
                        />
                        <select
                          value={raceEdit.priority}
                          onChange={(e) => setRaceEdit((p) => ({ ...p, priority: e.target.value as Race['priority'] }))}
                          className="input select w-20"
                          aria-label="Priority"
                        >
                          <option value="A">A</option>
                          <option value="B">B</option>
                          <option value="C">C</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => saveRaceEdit(race.id)}
                          disabled={savingRaceEdit}
                          className="btn-primary"
                        >
                          {savingRaceEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {raceError && (
              <p className="mb-3 text-sm text-rose-600 dark:text-rose-400">{raceError}</p>
            )}

            <div className="flex flex-wrap gap-3">
              <input
                type="text"
                value={newRace.name}
                onChange={(e) => setNewRace((r) => ({ ...r, name: e.target.value }))}
                className="input min-w-[200px] flex-[2]"
                placeholder="Race name"
              />
              <input
                type="text"
                value={newRace.goal}
                onChange={(e) => setNewRace((r) => ({ ...r, goal: e.target.value }))}
                className="input min-w-[140px] flex-1"
                placeholder="Goal (optional)"
                title="Target time or outcome, e.g. 3:55, sub-4, finish strong"
              />
              <input
                type="date"
                value={newRace.race_date}
                onChange={(e) => setNewRace((r) => ({ ...r, race_date: e.target.value }))}
                className="input w-40 shrink-0"
              />
              <input
                type="number"
                value={newRace.distance_miles}
                onChange={(e) => setNewRace((r) => ({ ...r, distance_miles: e.target.value }))}
                className="input w-24 shrink-0"
                placeholder="Miles"
                min="0"
                step="0.1"
              />
              <select
                value={newRace.priority}
                onChange={(e) => setNewRace((r) => ({ ...r, priority: e.target.value as Race['priority'] }))}
                className="input select w-20 shrink-0"
                aria-label="Priority"
              >
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
              </select>
              <button
                type="button"
                onClick={handleAddRace}
                disabled={addingRace || !newRace.name || !newRace.race_date}
                className="btn-primary shrink-0"
              >
                {addingRace ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Add
              </button>
            </div>
          </section>

          <section className="card p-6 sm:p-8">
            <h2 className="mb-2 flex items-center gap-2 text-base font-semibold text-zinc-900 dark:text-white">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600/10 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-400">
                <CalendarRange className="h-4 w-4" />
              </span>
              Training plan calendar
            </h2>
            <p className="mb-4 text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
              Already following a plan in Runna or another training app? Paste
              its calendar feed and your coach will treat that plan as the
              default — then use your sleep, HRV, and readiness to confirm each
              day&apos;s session or adjust it when your body isn&apos;t ready.
              In Runna: <span className="font-medium text-zinc-700 dark:text-zinc-300">
              Connected Apps &amp; Devices → Calendars → Other Calendar → copy the iCal URL</span>.
            </p>
            <input
              type="url"
              name="trainingPlanIcalUrl"
              value={formData.trainingPlanIcalUrl}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, trainingPlanIcalUrl: e.target.value }))
              }
              className="input"
              placeholder="https://… or webcal://… (iCal feed URL)"
            />
            <p className="mt-2 text-[11px] text-zinc-400">
              This URL is personal to you — treat it like a password. Leave
              blank to go back to fully coach-generated plans.
            </p>
          </section>

          {ingestUrl && (
            <section className="card p-6 sm:p-8">
              <h2 className="mb-2 flex items-center gap-2 text-base font-semibold text-zinc-900 dark:text-white">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-600/10 text-sky-600 dark:bg-sky-400/10 dark:text-sky-400">
                  <Smartphone className="h-4 w-4" />
                </span>
                Apple Health (iPhone)
              </h2>
              <p className="mb-4 text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
                Wear a Garmin or Apple Watch? Your sleep, recovery, and
                workouts can sync to FitScript automatically through Apple
                Health — a one-time, two-minute setup with the{' '}
                <span className="font-medium text-zinc-700 dark:text-zinc-300">Health Auto Export</span> app
                and your personal sync address below. Step-by-step guide
                coming to the Help Center.
              </p>
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded-xl bg-zinc-100 px-3 py-2.5 text-xs text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                  {ingestUrl}
                </code>
                <button type="button" onClick={copyIngestUrl} className="btn-secondary shrink-0">
                  {copiedIngestUrl ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copiedIngestUrl ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p className="mt-2 text-[11px] text-zinc-400">
                This URL contains your personal key — treat it like a password.
              </p>
            </section>
          )}

          <ServiceConnection user={user} />

          <div className="flex justify-end pb-8">
            <button type="submit" disabled={isSaving} className="btn-primary px-6 py-3">
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save changes
                </>
              )}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
